// Catalog of card lists the app can load. Started as a single
// bilingual list ("general") backed by a published Google Sheet CSV;
// now carries three orthogonal dimensions each deck can toggle:
//
//   - shape         — "bilingual" (two language faces, FR↔BG) vs
//                     "mono" (two same-language faces, both in the
//                     user's UI locale; e.g. capitals: country →
//                     capital).
//   - direction     — "bidirectional" (either face can prompt; the
//                     direction slider is shown) vs "unidirectional"
//                     (fixed face order; slider hidden).
//   - responseMode  — "open" (type the answer — current behaviour) vs
//                     "mcq" (multiple-choice; not wired yet).
//
// Faces describe the deck's two sides. For bilingual the `key` IS the
// Lang code and `lang` is set so TTS works; the card's CSV columns are
// keyed by the same Lang codes. For mono, `key` is an arbitrary slug
// ("country", "capital", "q", "a") used both in the CSV column prefix
// and as the runtime face key; `lang` is omitted (both faces use the
// UI locale).
//
// This abstraction exists so the future picker UI (termes médicaux,
// plantes, géographie, user-scoped decks, …) and the eventual
// Supabase-backed storage slot in without structural changes. For now,
// the catalog is a const; post-Supabase it becomes a DB query, with
// user-scoped rows filtered by RLS.
//
// `scope: "shared"` = every user sees the same data.
// `scope: "user"`   = (future) list lives in a per-user namespace.

import type { Lang } from "./langs";

export type ListScope = "shared" | "user";

export type ListShape = "bilingual" | "mono";
export type ListDirection = "bidirectional" | "unidirectional";
export type ListResponseMode = "open" | "mcq";

export type FaceSchema = {
  /** Runtime face key. For bilingual = Lang code (e.g. "fr-FR"). For
   *  mono = arbitrary slug (e.g. "country", "capital"); the CSV
   *  columns for that face are `{key}_{locale}` for each supported
   *  UI locale. */
  key: string;
  /** i18n key for the display name of this face (arrow labels,
   *  pickers). For bilingual this is typically one of LANG_SHORT keys;
   *  for mono it's a deck-specific key (e.g. "face_country"). */
  labelKey: string;
  /** TTS hint. Set for bilingual (so the audio uses the right voice);
   *  omitted for mono (the face text is in the user's UI locale and
   *  can use the locale-derived voice). */
  lang?: Lang;
};

export type CardList = {
  /** Stable key used in localStorage + (future) DB rows / URLs. */
  id: string;
  /** Theme grouping — languages / geography / … (cf. config/themes.ts).
   *  Strong-theme model: the theme dictates the deck's shape, so the
   *  deck's own `shape` field MUST match THEMES[themeId].shape. */
  themeId: string;
  /** i18n key for the display name in the picker UI. */
  nameKey: string;
  /** Source URL. Today: published Google Sheet CSV. Post-migration:
   *  replaced by a DB query keyed on `id`. A single URL regardless of
   *  shape — the sheet itself is multi-locale (columns suffixed by
   *  locale for mono, columns keyed by Lang for bilingual). */
  csvUrl: string;
  scope: ListScope;
  /** See module doc. Must match THEMES[themeId].shape — kept on the
   *  deck for fast reads without forcing every caller to look up the
   *  theme. */
  shape: ListShape;
  /** See module doc. Default "bidirectional". */
  direction: ListDirection;
  /** See module doc. Default "open". */
  responseMode: ListResponseMode;
  /** Override for the direction-picker label shown above the preset
   *  pills (profile modal). Keep it specific to the deck's subject so
   *  the user grasps what the pills mean — e.g. "Sens de traduction"
   *  for a language deck, "Pays ou capitale ?" for capitales. When
   *  absent, falls back to a generic bilingual / mono default. */
  dirLabelKey?: string;
  /** Whether to expose the "Ajouter" entry in the menu for this deck.
   *  Default true. Curated factual decks (capitales, départements,
   *  US states…) set this to false — new entries arrive via deck
   *  curation, not user submissions. */
  allowAdd?: boolean;
  /** Default number of choices rendered in MCQ mode (only consulted
   *  when `responseMode === "mcq"`). Choice of value per deck — 4 is
   *  the trivia-quiz classic; 3 often enough for language decks. */
  mcqCount?: number;
  /** MCQ distractor source: "preset" (stored on the card under an
   *  extras field) vs "auto" (pick random other cards from the same
   *  deck). Only consulted when `responseMode === "mcq"`. */
  mcqDistractors?: "preset" | "auto";
  /** The deck's two faces, in presentation order. For bilingual the
   *  face keys double as card CSV column names. For mono the face
   *  keys prefix the locale-suffixed CSV columns (e.g. face "country"
   *  → columns `country_fr`, `country_bg`, …). */
  faces: readonly [FaceSchema, FaceSchema];
};

export const LIST_CATALOG: readonly CardList[] = [
  {
    id: "general",
    themeId: "languages",
    nameKey: "list_general",
    csvUrl:
      "https://docs.google.com/spreadsheets/d/e/2PACX-1vRWdgKJ7g3WXqELYSYQ04gayUAXS9zeIhLhZS-AdJTAO9Tonz687369sC6Xf1ZGFU8CrfzNHxWCKGOq/pub?gid=0&single=true&output=csv",
    scope: "shared",
    shape: "bilingual",
    direction: "bidirectional",
    responseMode: "open",
    faces: [
      // Face keys intentionally match Lang codes so the existing
      // `card.faces[Lang]` lookups keep working unchanged. The label
      // keys reuse the lang-name i18n entries (LANG_NAMES).
      { key: "fr-FR", labelKey: "lang_fr-FR", lang: "fr-FR" },
      { key: "bg-BG", labelKey: "lang_bg-BG", lang: "bg-BG" },
    ],
  },
  {
    id: "capitales",
    themeId: "geography",
    nameKey: "list_capitals",
    // First mono-shaped deck: 197 countries × 3 UI locales, published
    // CSV with `{face_key}_{locale}` columns. The parser picks the
    // right two columns based on currentLocale() at load time.
    csvUrl:
      "https://docs.google.com/spreadsheets/d/e/2PACX-1vRN_T0TVLtHUDvG-k-XKwWjAovbOnSmyzLSxj5-Uc9_Ev3BSsv8r0DsWUQ3UtAeXkFFvacgj5cVafbK/pub?output=csv",
    scope: "shared",
    shape: "mono",
    direction: "bidirectional",
    responseMode: "open",
    dirLabelKey: "capitales_dir_label",
    allowAdd: false,
    faces: [
      { key: "country", labelKey: "face_country" },
      { key: "capital", labelKey: "face_capital" },
    ],
  },
  {
    id: "departements",
    themeId: "geography",
    nameKey: "list_departements",
    csvUrl:
      "https://docs.google.com/spreadsheets/d/e/2PACX-1vSC5OPvTfJp8gZ3RvJM0F8-xYMcI8V6T9toSSf6OZTOjvrMUuuCiQTjsLOMxgnJG4-RJheAeFHQKu2o/pub?output=csv",
    scope: "shared",
    shape: "mono",
    direction: "bidirectional",
    responseMode: "open",
    dirLabelKey: "departements_dir_label",
    allowAdd: false,
    faces: [
      { key: "departement", labelKey: "face_departement" },
      { key: "prefecture", labelKey: "face_prefecture" },
    ],
  },
  {
    id: "etats-us",
    themeId: "geography",
    nameKey: "list_etats_us",
    csvUrl:
      "https://docs.google.com/spreadsheets/d/e/2PACX-1vT_mEwVbwo8jWVeVLWQ9iMk7r99z3KWOQk6ik1wOnBn0MpJjaDUaVQ7QorDMzDT2tqGNzwjOQC-ZQcT/pub?output=csv",
    scope: "shared",
    shape: "mono",
    direction: "bidirectional",
    responseMode: "open",
    dirLabelKey: "etats_us_dir_label",
    allowAdd: false,
    faces: [
      { key: "state", labelKey: "face_state" },
      { key: "capital", labelKey: "face_capital" },
    ],
  },
];

export const DEFAULT_LIST_ID = "general";

/** Resolve a list by id, falling back to the first catalog entry if the
 *  id is unknown (stale localStorage pointer from a list that was
 *  removed, typo, etc.). */
export function getList(id: string): CardList {
  return LIST_CATALOG.find((l) => l.id === id) ?? LIST_CATALOG[0]!;
}

/** Decks belonging to a theme, in catalog order. Used by the picker
 *  to render only the decks matching the user's current theme. */
export function listsForTheme(themeId: string): readonly CardList[] {
  return LIST_CATALOG.filter((l) => l.themeId === themeId);
}

/** Default deck for a theme — used when the user switches themes and
 *  we need to auto-select a deck in the new theme. Falls back to the
 *  first catalog entry if the theme has no decks yet. */
export function defaultListForTheme(themeId: string): CardList {
  return listsForTheme(themeId)[0] ?? LIST_CATALOG[0]!;
}
